import { MACHINE_THRESHOLDS, RESULT } from './thresholds.js';
import { evaluateMetric, overallResult } from './scoring.js';

const BROWSER_FLOORS = {
  Chrome: { currentMajor: 148, floorMajor: 124, label: 'Chrome 124 or newer' },
  Edge: { currentMajor: 148, floorMajor: 124, label: 'Edge 124 or newer' },
  Firefox: { currentMajor: 150, floorMajor: 126, label: 'Firefox 126 or newer' },
  Safari: { currentVersion: 26.4, floorVersion: 17.5, label: 'Safari 17.5 or newer' }
};

function getRuntimeValue(runtime, key) {
  return runtime && key in runtime ? runtime[key] : undefined;
}

function parseMajor(version) {
  const major = Number.parseInt(String(version || '').split('.')[0], 10);
  return Number.isFinite(major) ? major : null;
}

function parseVersionNumber(version) {
  const parts = String(version || '').split('.');
  const major = Number.parseInt(parts[0], 10);
  const minor = Number.parseInt(parts[1] || '0', 10);
  if (!Number.isFinite(major) || !Number.isFinite(minor)) return null;
  return Number(`${major}.${minor}`);
}

export function parseBrowserVersion(userAgent = '') {
  const ua = String(userAgent || '');
  const edge = ua.match(/Edg\/([\d.]+)/);
  if (edge) {
    const version = edge[1];
    return { browser: 'Edge', version, major: parseMajor(version), label: `Edge ${version}` };
  }

  const chrome = ua.match(/Chrome\/([\d.]+)/);
  if (chrome && !/OPR\//.test(ua)) {
    const version = chrome[1];
    return { browser: 'Chrome', version, major: parseMajor(version), label: `Chrome ${version}` };
  }

  const firefox = ua.match(/Firefox\/([\d.]+)/);
  if (firefox) {
    const version = firefox[1];
    return { browser: 'Firefox', version, major: parseMajor(version), label: `Firefox ${version}` };
  }

  const safari = ua.match(/Version\/([\d.]+).*Safari\//);
  if (safari && !/Chrome\//.test(ua)) {
    const version = safari[1];
    return { browser: 'Safari', version, major: parseMajor(version), label: `Safari ${version}` };
  }

  return { browser: 'Unknown', version: null, major: null, label: 'Unknown browser' };
}

export function estimateBrowserAgeMonths(parsed) {
  const floor = BROWSER_FLOORS[parsed?.browser];
  if (!floor) return null;

  if (parsed.browser === 'Safari') {
    const version = parseVersionNumber(parsed.version);
    if (version === null) return null;
    if (version >= floor.currentVersion) return 0;
    if (version >= 18) return 12;
    if (version >= floor.floorVersion) return 24;
    return 25;
  }

  if (parsed.major === null) return null;
  return Math.max(0, floor.currentMajor - parsed.major);
}

export function getBrowserPreflight({
  userAgent,
  runtime = typeof globalThis !== 'undefined' ? globalThis : {}
} = {}) {
  const ua = userAgent
    ?? getRuntimeValue(getRuntimeValue(runtime, 'navigator'), 'userAgent')
    ?? '';
  const parsed = parseBrowserVersion(ua);

  if (!getRuntimeValue(runtime, 'fetch')) {
    return { supported: false, reason: 'This browser does not support the network APIs required for the test.', parsed };
  }
  if (!getRuntimeValue(runtime, 'WebSocket')) {
    return { supported: false, reason: 'This browser does not support WebSocket connections required for the notifier test.', parsed };
  }
  if (!getRuntimeValue(runtime, 'RTCPeerConnection')) {
    return { supported: false, reason: 'This browser does not support WebRTC, which is required for the voice-quality test.', parsed };
  }

  const floor = BROWSER_FLOORS[parsed.browser];
  if (floor) {
    if (parsed.browser === 'Safari') {
      const version = parseVersionNumber(parsed.version);
      if (version !== null && version < floor.floorVersion) {
        return { supported: false, reason: `Use ${floor.label} to run this test.`, parsed };
      }
    } else if (parsed.major !== null && parsed.major < floor.floorMajor) {
      return { supported: false, reason: `Use ${floor.label} to run this test.`, parsed };
    }
  }

  return { supported: true, reason: null, parsed };
}

function evaluateMachineMetric(value, threshold) {
  if (threshold.direction === 'boolean-is-true') {
    if (value === null || value === undefined) return RESULT.NO_DATA;
    return value === true ? RESULT.PASS : RESULT.FAIL;
  }
  return evaluateMetric(value, threshold);
}

export function evaluateMachineFitness(metrics = {}) {
  const verdicts = {};
  for (const [key, threshold] of Object.entries(MACHINE_THRESHOLDS)) {
    verdicts[key] = evaluateMachineMetric(metrics[key], threshold);
  }

  return {
    ...metrics,
    cpuVerdict: verdicts.cpuOpsPerSec,
    machineVerdict: overallResult(verdicts),
    verdicts
  };
}

function scheduleYield() {
  return new Promise(resolve => setTimeout(resolve, 0));
}

export async function measureCpuOpsPerSec({
  durationMs = 45_000,
  sliceMs = 75,
  now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
  yieldToBrowser = scheduleYield,
  onProgress = () => {}
} = {}) {
  const start = now();
  const end = start + Math.max(1, durationMs);
  let ops = 0;
  const samples = [];
  let guard = 0;

  while (now() < end && guard < 100000) {
    guard += 1;
    const sliceStart = now();
    let sliceOps = 0;
    let accumulator = 0;

    while (now() - sliceStart < sliceMs && now() < end) {
      for (let i = 0; i < 1000; i += 1) {
        accumulator = (accumulator + Math.imul((i + sliceOps) | 0, 2654435761)) | 0;
      }
      sliceOps += 1000;
    }

    ops += sliceOps;
    const elapsedMs = Math.max(1, now() - start);
    samples.push({ elapsedMs: Math.round(elapsedMs), ops: sliceOps, checksum: accumulator });
    onProgress({ elapsedMs, opsPerSec: Math.round(ops / (elapsedMs / 1000)) });

    if (now() < end) {
      await yieldToBrowser();
    }
  }

  const elapsedMs = Math.max(1, now() - start);
  return {
    cpuOpsPerSec: Math.round(ops / (elapsedMs / 1000)),
    cpuSamples: samples
  };
}

function detectWebgl(documentRef) {
  if (!documentRef?.createElement) {
    return { webgl2Supported: false, hardwareAccelerationEnabled: false, renderer: null };
  }

  try {
    const canvas = documentRef.createElement('canvas');
    const context = canvas.getContext?.('webgl2', { failIfMajorPerformanceCaveat: true });
    if (!context) {
      return { webgl2Supported: false, hardwareAccelerationEnabled: false, renderer: null };
    }

    const ext = context.getExtension?.('WEBGL_debug_renderer_info');
    const renderer = ext ? context.getParameter?.(ext.UNMASKED_RENDERER_WEBGL) : null;
    const softwareRenderer = renderer ? /swiftshader|software|llvmpipe|mesa offscreen/i.test(renderer) : false;
    return {
      webgl2Supported: true,
      hardwareAccelerationEnabled: !softwareRenderer,
      renderer: renderer || null
    };
  } catch {
    return { webgl2Supported: false, hardwareAccelerationEnabled: false, renderer: null };
  }
}

async function detectIncognito(navigatorRef) {
  try {
    if (!navigatorRef?.storage?.estimate) return null;
    const estimate = await navigatorRef.storage.estimate();
    if (!estimate?.quota) return null;
    return estimate.quota < 120 * 1024 * 1024;
  } catch {
    return null;
  }
}

async function detectBattery(navigatorRef) {
  try {
    if (!navigatorRef?.getBattery) return null;
    const battery = await navigatorRef.getBattery();
    return battery?.charging === false;
  } catch {
    return null;
  }
}

function memoryHeadroomBytes(runtime) {
  const memory = getRuntimeValue(getRuntimeValue(runtime, 'performance'), 'memory');
  if (!memory) return null;
  const limit = Number(memory.jsHeapSizeLimit);
  const used = Number(memory.usedJSHeapSize);
  if (!Number.isFinite(limit) || !Number.isFinite(used) || limit < used) return null;
  return Math.round(limit - used);
}

async function codecSupport(navigatorRef) {
  const mediaCapabilities = navigatorRef?.mediaCapabilities;
  if (!mediaCapabilities?.decodingInfo) {
    return { video: [], audio: [] };
  }

  const videoConfigs = [
    {
      codec: 'vp9',
      config: {
        type: 'file',
        video: { contentType: 'video/webm; codecs="vp9"', width: 1280, height: 720, bitrate: 2500000, framerate: 30 }
      }
    },
    {
      codec: 'h264',
      config: {
        type: 'file',
        video: { contentType: 'video/mp4; codecs="avc1.42E01E"', width: 1280, height: 720, bitrate: 2500000, framerate: 30 }
      }
    }
  ];
  const audioConfigs = [
    {
      codec: 'opus',
      config: {
        type: 'file',
        audio: { contentType: 'audio/ogg; codecs="opus"', channels: 2, bitrate: 128000, samplerate: 48000 }
      }
    }
  ];

  const check = async ({ codec, config }) => {
    try {
      const info = await mediaCapabilities.decodingInfo(config);
      return {
        codec,
        supported: Boolean(info.supported),
        smooth: Boolean(info.smooth),
        powerEfficient: Boolean(info.powerEfficient)
      };
    } catch {
      return { codec, supported: false, smooth: false, powerEfficient: false };
    }
  };

  return {
    video: await Promise.all(videoConfigs.map(check)),
    audio: await Promise.all(audioConfigs.map(check))
  };
}

function computeClutterScore({
  cpuVerdict,
  hardwareAccelerationEnabled,
  incognitoDetected,
  onBattery,
  memoryHeadroomBytes
}) {
  let score = 0;
  if (cpuVerdict === RESULT.BORDERLINE) score += 15;
  if (cpuVerdict === RESULT.FAIL) score += 30;
  if (hardwareAccelerationEnabled === false) score += 20;
  if (incognitoDetected === true) score += 10;
  if (onBattery === true) score += 10;
  if (typeof memoryHeadroomBytes === 'number') {
    if (memoryHeadroomBytes < 512 * 1024 * 1024) score += 25;
    else if (memoryHeadroomBytes < 1024 * 1024 * 1024) score += 10;
  }
  return Math.min(100, score);
}

export async function collectMachineFitness({
  durationMs = 45_000,
  runtime = typeof globalThis !== 'undefined' ? globalThis : {},
  navigatorRef = getRuntimeValue(runtime, 'navigator'),
  documentRef = getRuntimeValue(runtime, 'document'),
  onProgress = () => {}
} = {}) {
  const userAgent = navigatorRef?.userAgent || '';
  const parsed = parseBrowserVersion(userAgent);
  const browserAgeMonths = estimateBrowserAgeMonths(parsed);
  const webgl = detectWebgl(documentRef);
  const asyncChecks = Promise.all([
    detectIncognito(navigatorRef),
    detectBattery(navigatorRef),
    codecSupport(navigatorRef)
  ]);
  const cpu = await measureCpuOpsPerSec({ durationMs, onProgress });
  const [incognitoDetected, onBattery, codecs] = await asyncChecks;

  const base = {
    cpuOpsPerSec: cpu.cpuOpsPerSec,
    deviceMemoryGb: navigatorRef?.deviceMemory ?? null,
    cpuCores: navigatorRef?.hardwareConcurrency ?? null,
    browserVersion: parsed.label,
    browserAgeMonths,
    hardwareAccelerationEnabled: webgl.hardwareAccelerationEnabled,
    webrtcSupported: Boolean(getRuntimeValue(runtime, 'RTCPeerConnection')),
    webcodecsSupported: Boolean(getRuntimeValue(runtime, 'VideoDecoder') || getRuntimeValue(runtime, 'AudioDecoder')),
    webgl2Supported: webgl.webgl2Supported,
    hwVideoCodecs: codecs.video,
    hwAudioCodecs: codecs.audio,
    incognitoDetected,
    onBattery,
    memoryHeadroomBytes: memoryHeadroomBytes(runtime)
  };
  const evaluated = evaluateMachineFitness(base);
  const clutterScore = computeClutterScore({ ...base, cpuVerdict: evaluated.cpuVerdict });

  return {
    ...evaluated,
    clutterScore,
    raw: {
      cpu_samples: cpu.cpuSamples,
      gpu_renderer: webgl.renderer,
      clutter_score: clutterScore
    }
  };
}

function stripPrivateFields(value) {
  if (Array.isArray(value)) {
    return value.map(stripPrivateFields);
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  const sanitized = {};
  for (const [key, child] of Object.entries(value)) {
    if (key === 'clutter_score' || key === 'clutterScore') continue;
    sanitized[key] = stripPrivateFields(child);
  }
  return sanitized;
}

export function sanitizeCandidateRawData(data = {}) {
  return {
    environment: data.environment || {},
    raw_metrics: stripPrivateFields(data.raw_metrics || {})
  };
}
